import { useDeId } from '@/components/ml/de-id';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useScrollAndFocus } from '@/components/ux/hooks/use-scroll-and-focus';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import { type Comment } from '@/models/registry';
import type { JSONContent } from '@tiptap/core';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import { motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import CommentMenu from './comment-menu';
import CommentReplyHeader from './comment-reply-header';
import ReplyDeIdEd from './reply-de-id-ed';
import AddReplyTipTap from './tip-tap-editor/add-reply-tiptap';
import EditCommentTiptap from './tip-tap-editor/edit-comment-tiptap';
import { usePaddockEditor } from './tip-tap-editor/use-paddock-editor';

const calculateWasCreatedRecently = (createdAt: string) => {
  return dayjs(createdAt).add(1, 'second').isAfter(dayjs());
};

export const useGetTextFromContent = (content: JSONContent) => {
  const editor = usePaddockEditor({
    initialContent: content,
    editable: false,
  });

  return editor.editor?.getText();
};

const ThreadDeIdEd = observer(
  ({ comment, dealId }: { comment: Comment; dealId: string }) => {
    const { content } = comment;
    const text = useGetTextFromContent(content);

    const replies = comment.replies;
    const { id: userId } = useUser();
    const threadIncludesReplyFromMe = replies.some(
      (reply) => reply.created_by && reply.created_by.id === userId,
    );

    const [showAddReply, setShowAddReply] = useState(threadIncludesReplyFromMe);

    const wasCreatedRecently = calculateWasCreatedRecently(comment.created_at);

    const shouldAnimate = wasCreatedRecently;

    const { shouldShowFocus, focusRef } = useScrollAndFocus({
      uiType: 'comment',
      id: comment.id,
    });

    const commentHoverRef = useRef<HTMLDivElement>(null);

    const [isEditingComment, setIsEditingComment] = useState(false);

    const deIdMutation = useDeId({});

    useEffect(() => {
      if (text) {
        deIdMutation.mutate(text);
      }
    }, [text]);

    return (
      <motion.div
        initial={{
          height: shouldAnimate ? 0 : 'auto',
          opacity: shouldAnimate ? 0 : 1,
        }}
        animate={{ height: 'auto', opacity: 1 }}
        onClick={() => setShowAddReply(true)}
        ref={focusRef}
      >
        <Card className={cn('p-4 flex flex-col gap-0 px-0 pb-0')}>
          <CardHeader
            className={cn('p-0 px-4', {
              'border-1 border-primary': shouldShowFocus,
            })}
            ref={commentHoverRef}
          >
            <CardTitle className="flex flex-row justify-between px-0">
              <CommentReplyHeader
                commentOrReply={comment}
                isEditingComment={isEditingComment}
              />

              <CommentMenu
                comment={comment}
                dealId={dealId}
                hoverRef={commentHoverRef}
                setIsEditingComment={setIsEditingComment}
              />
            </CardTitle>
            {isEditingComment ? (
              <EditCommentTiptap
                dealId={dealId}
                comment={comment}
                setIsEditingComment={setIsEditingComment}
              />
            ) : (
              <CardDescription className="pb-4 whitespace-pre-wrap text-uneditable">
                {/* <MessageTipTap content={content} /> */}
                <div>{deIdMutation.data}</div>
              </CardDescription>
            )}
          </CardHeader>

          {!isEmpty(replies) && (
            <>
              <div className="border-t w-full"></div>
              {replies.map((reply) => {
                const shouldAnimate = calculateWasCreatedRecently(
                  reply.created_at,
                );

                return (
                  <motion.div
                    key={reply.id}
                    initial={{
                      height: shouldAnimate ? 0 : 'auto',
                      opacity: shouldAnimate ? 0 : 1,
                    }}
                    animate={{ height: 'auto', opacity: 1 }}
                  >
                    <ReplyDeIdEd reply={reply} dealId={dealId} />
                  </motion.div>
                );
              })}
            </>
          )}

          {showAddReply && (
            <motion.div
              className="border-t px-4"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <AddReplyTipTap commentId={comment.id} dealId={dealId} />
            </motion.div>
          )}
        </Card>
      </motion.div>
    );
  },
);

export default ThreadDeIdEd;
